import { Button, CircularProgress, FormControlLabel, makeStyles, Switch, Tooltip } from '@material-ui/core';
import { SaveOutlined as SaveIcon } from '@material-ui/icons';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import React, { ChangeEvent, useCallback, useContext, useEffect, useState } from 'react';
import LocalizationContext from '../../../../contexts/LocalizationContext';
import IFormSubmitResult from '../../../../interfaces/IFormSubmitResult';
import inSection from '../../../hoc/inSection';
import FormRowLabel from '../../../main/FormRowLabel';
import SubmitFailedDialog from '../../../main/SubmitFailedDialog';
import { AccountContext, PreferencesContext } from '../context/AccountProvider';

interface PreferencesState {
    submitResult?: IFormSubmitResult;
    isSaving: boolean;
}

const useStyles = makeStyles({
    tooltip: {
        textAlign: 'justify',
        borderRadius: '4px',
        boxShadow: '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
        maxWidth: '600px',
        fontSize: '13px',
        padding: '10px 14px',
        fontFamily: '"Open Sans", "Roboto", "Helvetica", "Arial", sans-serif',
        color: '#dddddd',
    },
    tooltipTitle: {
        fontSize: '14px',
        fontWeight: 500,
        color: 'white !important',
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
    },
    preferenceSwitch: {
        marginTop: 0,
        marginBottom: 0,
    },
});

const Preferences = () => {
    const account = useContext(AccountContext);
    const localization = useContext(LocalizationContext);
    const [state, setState] = useState<PreferencesState>({
        submitResult: undefined,
        isSaving: false,
    });
    const accountPreferences = account.preferences;
    const getAccountPreferences = account.getPreferences;
    const setAccountPreferences = account.setPreferences;
    const [preferences, setPreferences] = useState<PreferencesContext>(account.preferences);
    const classes = useStyles();

    const isDirty: boolean =
        preferences.annualObjectiveNotifEnabled !== account.preferences.annualObjectiveNotifEnabled ||
        preferences.followUpNotifEnabled !== account.preferences.followUpNotifEnabled ||
        preferences.careerNotifEnabled !== account.preferences.careerNotifEnabled ||
        preferences.overallAssessementNotifEnabled !== account.preferences.overallAssessementNotifEnabled ||
        preferences.myTeamNotifEnabled !== account.preferences.myTeamNotifEnabled;

    // Get initial
    useEffect(() => {
        getAccountPreferences(
            (_: PreferencesContext) => {},
            (submitResult: IFormSubmitResult) => setState((s: PreferencesState) => ({ ...s, submitResult: submitResult }))
        );
    }, [getAccountPreferences]);

    useEffect(() => {
        setPreferences((p: PreferencesContext) => ({ ...p, ...accountPreferences }));
    }, [accountPreferences]);

    const saveChanges = useCallback(() => {
        setState((s: PreferencesState) => ({ ...s, isSaving: true }));
        setAccountPreferences(
            preferences,
            () => setState((s: PreferencesState) => ({ ...s, isSaving: false })),
            (submitResult: IFormSubmitResult) => setState((s: PreferencesState) => ({ ...s, submitResult: submitResult, isSaving: false }))
        );
    }, [setState, setAccountPreferences, preferences]);

    return (
        <>
            <FormRowLabel text={localization.strings.preferences.emailNotification} style={{ marginBottom: '12px' }} />
            <div className={classes.container}>
                <Tooltip
                    arrow
                    placement='top-start'
                    classes={{ tooltip: classes.tooltip }}
                    title={
                        <>
                            <FormRowLabel
                                text={localization.strings.preferences.feedbackNotifSettingTitle}
                                className={classes.tooltipTitle}
                                style={{ marginTop: '0' }}
                            />
                            {localization.strings.preferences.feedbackNotifSettingText}
                        </>
                    }>
                    <FormControlLabel
                        className={classes.preferenceSwitch}
                        control={<Switch disabled checked />}
                        label={localization.strings.feedbacks.feedbacks}
                    />
                </Tooltip>
                <FormControlLabel
                    className={classes.preferenceSwitch}
                    control={
                        <Switch
                            checked={preferences.annualObjectiveNotifEnabled}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setPreferences((s: PreferencesContext) => ({ ...s, annualObjectiveNotifEnabled: e.target.checked }));
                            }}
                        />
                    }
                    label={localization.strings.objectives.objectives}
                />
                <FormControlLabel
                    className={classes.preferenceSwitch}
                    control={
                        <Switch
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setPreferences((s: PreferencesContext) => ({ ...s, followUpNotifEnabled: e.target.checked }));
                            }}
                            checked={preferences.followUpNotifEnabled}
                        />
                    }
                    label={localization.strings.followUp.followUp}
                />
                <FormControlLabel
                    className={classes.preferenceSwitch}
                    control={
                        <Switch
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setPreferences((s: PreferencesContext) => ({ ...s, careerNotifEnabled: e.target.checked }));
                            }}
                            checked={preferences.careerNotifEnabled}
                        />
                    }
                    label={localization.strings.career.career}
                />
                <FormControlLabel
                    className={classes.preferenceSwitch}
                    control={
                        <Switch
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setPreferences((s: PreferencesContext) => ({ ...s, overallAssessementNotifEnabled: e.target.checked }));
                            }}
                            checked={preferences.overallAssessementNotifEnabled}
                        />
                    }
                    label={localization.strings.overallAssessment.overallAssessment}
                />
                <FormControlLabel
                    className={classes.preferenceSwitch}
                    control={
                        <Switch
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setPreferences((s: PreferencesContext) => ({ ...s, myTeamNotifEnabled: e.target.checked }));
                            }}
                            checked={preferences.myTeamNotifEnabled}
                        />
                    }
                    label={localization.strings.myTeam.myTeam}
                />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                {state.isSaving ? (
                    <CircularProgress size={24} style={{ margin: '7px' }} />
                ) : (
                    <Button onClick={saveChanges} variant='contained' color='primary' disabled={!isDirty}>
                        <SaveIcon style={{ marginRight: '8px' }} />
                        {localization.strings.preferences.recordPreferences}
                    </Button>
                )}
            </div>

            <SubmitFailedDialog submitResult={state.submitResult} onClose={() => setState({ ...state, submitResult: undefined })} />
        </>
    );
};

export default inSection(Preferences, { icon: FavoriteBorderIcon, sectionTitleString: 'preferences' }, true);
